import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Stack } from '@mui/material';
import clsx from 'clsx';
import RatingChecklist from 'feature/document/DocumentContainer/DocumentChecklist/RatingChecklist';
import { Button } from 'global/components/Button';
import CommentContainer from 'global/components/CommentContainer';
import Form from 'global/components/form';
import { EmptySquareIcon } from 'global/components/PerformIcons';
import { PermissionControlledDisplay } from 'global/components/permission-gate';
import { Text } from 'global/components/text';
import TranslationManager from 'global/components/TranslationManager';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useUser } from 'global/hooks/useUser';
import { first, get } from 'global/lib/lodash';
import Styles, { useScreen } from 'global/styles';
import { useEffect, useState } from 'react';
import { UploadFiles } from 'redux/actions/imageUploader';
import sendData from 'redux/actions/sendData';
import { useStyles } from './styles';

const CheckpointInfoModal = (props) => {
  const { open } = props;
  return !open ? null : <CheckpointInfoModalDetail {...props} />;
};

const CheckpointInfoModalDetail = (props) => {
  const { open, checkpoint, pssvCheckpoint, onClose, scorecard, checklist, edition } = props;
  const [optionVals, setOptionVals] = useState(props.optionVals || []);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialLanguageData, setInitialLanguageData] = useState({});
  const [modifiedLanguageData, setModifiedLanguageData] = useState({});
  const [formState, setFormState] = useState({});

  const classes = Styles();
  const styles = useStyles();
  const { isMobile } = useScreen();
  // Context: When coming from the scoring interface, we may be receiving checkpoint information from a processed-scorecard-score-value
  // Otherwise, we default to using checkpoint
  const inheritedCheckpointData = pssvCheckpoint || checkpoint;
  const checkpointId = get(inheritedCheckpointData, pssvCheckpoint ? 'checkpointId' : 'id');
  const editionId = get(scorecard, 'editionId');
  const ownerRealm = Number(get(scorecard, 'ownerRealmId', get(edition, 'ownerRealmId', null)));

  // Context: When coming from the Library, `edition` is defined and `scorecard` is undefined.
  // When coming from the Scoring page, `scorecard` is defined and `edition` is undefined.
  const config = scorecard?.config || edition?.config;

  const checkpointData = useRetrieveData(
    { url: `/admin/checkpoints/${checkpointId}` },
    {
      build: ['checkpoints'],
      params: {
        editMode: 1,
        editionId: editionId,
        include: 'images,checkpointOptions',
      },
      transformData: (d) => {
        const item = first(d);
        if (!item) return item;
        const translations = { ...get(item, 'description', {}) };
        delete translations.en;
        // Return a new object — avoid mutating the cached API data and avoid side effects
        return { ...item, description: item.description?.en, _translations: translations };
      },
    }
  );

  useEffect(() => {
    const item = checkpointData.data;
    if (!item) return;
    const translations = item._translations || {};
    setInitialLanguageData(translations);
    setModifiedLanguageData(translations);
    setFormState({ ...item });
  }, [checkpointData.data]);

  const { userBuilderRealms: builders } = useUser();

  // When coming from the Library and using a rating checklist, select the single checkpoint whose `checkpointId` matches an entry in `checklist.checkpoints`.
  // When coming from the Scoring page, `checkpointData` is already the selected checkpoint.
  const selectedCheckpoint = edition ? get(checklist, 'checkpoints', []).find((c) => c.id === checkpointId) : get(checkpointData, 'data');

  const editVals = get(checkpointData, 'data.editMode', []);
  const canEditCheckpoint = get(editVals, 'owned', false);
  // Users assigned multiple organizations can edit comments, despite not editing the checkpoint directly
  const canEditComments = canEditCheckpoint || builders.some((b) => Number(b.id) === ownerRealm);

  const longTitle = get(checkpointData, 'data.name', '').length > 2;

  // If we're viewing this from the scoring interface, we need to build the optionVals from the scorecard min and max scores.
  useEffect(() => {
    if (scorecard && optionVals.length === 0) {
      const min = parseInt(get(scorecard, 'config.minScore', 0));
      const max = parseInt(get(scorecard, 'config.maxScore', 1));
      const step = parseInt(get(scorecard, 'config.step', 1)) || 1;

      const vals = [];
      const range = max - min;
      for (let i = min; i <= max; i += step) {
        vals.push(range > 0 ? Math.round(((i - min) / range) * 100) : 0);
      }
      setOptionVals(vals);
    }
  }, [scorecard]);

  const isRating = optionVals.length > 0 && !(get(config, 'minScore') === 0 && get(config, 'maxScore') === 1);

  const handleFormSubmit = async () => {
    setIsEditMode(false);

    const notes = get(formState, 'notes', '');
    const images = get(formState, 'images') || [];
    const deletedLanguages = Object.keys(initialLanguageData).filter((lang) => !(lang in modifiedLanguageData));
    const [firstLanguage, ...otherLanguages] = Object.keys(modifiedLanguageData);

    const buildCheckpointInfoData = (attributes) => ({
      data: {
        id: checkpointId.toString(),
        type: 'checkpoints',
        attributes,
      },
    });

    for (const languageCode of deletedLanguages) {
      await sendData({
        url: `/admin/checkpoints/${checkpointId}`,
        method: 'patch',
        data: buildCheckpointInfoData({ description: null, edition_id: editionId, lang: languageCode }),
      });
    }

    // First (or only) request always carries notes + images; optionally includes first language
    const firstPayload = buildCheckpointInfoData({
      notes,
      images,
      edition_id: editionId,
      ...(firstLanguage && { description: modifiedLanguageData[firstLanguage], lang: firstLanguage }),
    });
    await UploadFiles(firstPayload);
    await sendData({ url: `/admin/checkpoints/${checkpointId}`, method: 'patch', data: firstPayload });

    for (const languageCode of otherLanguages) {
      await sendData({
        url: `/admin/checkpoints/${checkpointId}`,
        method: 'patch',
        data: buildCheckpointInfoData({ description: modifiedLanguageData[languageCode], edition_id: editionId, lang: languageCode }),
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullScreen={isMobile} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" width="100%" alignItems="center">
          <Box className={styles.leftIcon}>
            <IconButton onClick={onClose}>
              <ArrowBackIcon />
            </IconButton>
          </Box>

          <Box className={styles.titleContainer}>
            <Text type="label_large">Checkpoint Info</Text>
          </Box>
        </Stack>
      </DialogTitle>
      <DialogContent className={styles.dialogContent}>
        <Form
          key={isEditMode ? 'edit' : 'view'}
          showAllScreens={false}
          showNavigation={false}
          loading={checkpointData.loading}
          onFormState={setFormState}
          loadingMessage={'Please Wait...'}
          screens={[
            {
              title: '',
              inputs: [
                {
                  id: 'checkpointTitle',
                  component: () => (
                    <Box>
                      <Text type="title_large">
                        <strong>
                          {get(inheritedCheckpointData, 'checklist.name', get(checklist, 'name', ''))} : {get(checkpointData, 'data.name', '')}
                        </strong>
                      </Text>
                    </Box>
                  ),
                },
                {
                  id: 'checkpointDescription',
                  component: () => (
                    <>
                      {isRating ? (
                        <Box>
                          <Text type="label_medium">Rating</Text>
                          <Text type="body_medium">{get(checkpointData, 'data.rating')}</Text>
                          <RatingChecklist
                            {...props}
                            editMode={false}
                            checklist={{ ...checklist, checkpoints: [selectedCheckpoint] }}
                            edition={{ ...edition, config: config }}
                            optionVals={optionVals}
                          />
                        </Box>
                      ) : (
                        <Box>
                          <div className={clsx(classes.row, classes.hlRow)}>
                            <div className={clsx(styles.iconContainer, styles.leftIcon)}>
                              <EmptySquareIcon />
                            </div>
                            <div className={classes.rowText}>
                              <strong className={!longTitle && classes.capitalize}>
                                {get(checkpointData, 'data.name', '')}
                                {!longTitle && `.`}
                                {` `}
                              </strong>
                              <span>{get(checkpointData, 'data.description', '')}</span>
                            </div>
                          </div>
                        </Box>
                      )}
                    </>
                  ),
                },

                {
                  id: 'translations',
                  props: {
                    label: 'Translations',
                  },
                  component: () => <TranslationManager isEditMode={isEditMode} modifiedLanguageData={modifiedLanguageData} setModifiedLanguageData={setModifiedLanguageData} />,
                },
                {
                  id: 'notes',
                  type: 'TextField',
                  defaultValue: () => get(formState, 'notes'),
                  props: {
                    label: 'Notes',
                    multiline: true,
                    placeholder: '',
                    disabled: !isEditMode,
                  },
                },
                {
                  id: 'images',
                  type: 'DropArea',
                  defaultValue: () => get(formState, 'images'),
                  props: {
                    label: 'Images',
                    notAllowed: ['tag'],
                    uploadAllNew: true,
                    collection: 'checkpoint-images',
                    shouldUpdateOnCaptionChange: false,
                    disabled: !isEditMode,
                    inputStyles: styles.paddingTop,
                    spaceBetween: 3,
                  },
                },
                {
                  id: 'editModePermissionGate',
                  component: () => {
                    return canEditCheckpoint ? (
                      <CheckpointInfoEditPermission
                        isEditMode={isEditMode}
                        onCancel={() => {
                          setIsEditMode(false);
                          setFormState({ ...checkpointData.data });
                          setModifiedLanguageData({ ...initialLanguageData });
                        }}
                        onSave={handleFormSubmit}
                        onEdit={() => setIsEditMode(true)}
                        styles={styles}
                        allowedPermissions={['manageAccount', 'createLibrary']}
                      />
                    ) : null;
                  },
                },
                {
                  id: 'comments',
                  component: () => {
                    return canEditComments ? <CheckpointInfoComments {...props} allowedPermissions={['manageAccount', 'createLibrary']} data={checkpointData.data} /> : null;
                  },
                },
              ],
            },
          ]}
        />
      </DialogContent>
    </Dialog>
  );
};

const CheckpointInfoEditPermission = PermissionControlledDisplay((props) => {
  const { isEditMode, onCancel, onSave, onEdit } = props;

  return (
    <Box display="flex" justifyContent="flex-end">
      {isEditMode ? (
        <Box display="flex" justifyContent="space-between" gap={1}>
          <Button variant="contained" className={props.styles.cancelButton} startIcon={<CloseIcon />} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave}>
            Save
          </Button>
        </Box>
      ) : (
        <Button variant="contained" startIcon={<EditIcon />} onClick={onEdit}>
          Edit
        </Button>
      )}
    </Box>
  );
});

const CheckpointInfoComments = PermissionControlledDisplay((props) => {
  const checkpointId = props.pssvCheckpoint?.checkpointId || props.checkpoint?.id;
  const [showCreateComment, setShowCreateComment] = useState(false);
  const [commentLoading, setCommentLoading] = useState(false);

  const uhook = useUser();
  const { me } = uhook;

  const [commentState, setCommentState] = useState({
    type: 'comments',
    attributes: {
      user_id: get(me, 'id'),
      commentable_id: checkpointId,
      commentable_type: 'checkpoint',
      body: '',
      images: [],
      meta: {},
    },
  });
  const commentsData = useRetrieveData(
    { url: `/comments` },
    {
      build: ['comments'],
      params: {
        filter: {
          commentable_id: checkpointId,
          commentable_type: 'checkpoint',
        },
        include: 'images,author',
      },
    }
  );

  // the comment argument here comes from CommentContainer when it calls onSubmitComment
  const handleSubmitComment = async (comment) => {
    try {
      setCommentLoading(true);
      const submitData = { data: comment };

      await UploadFiles(submitData, { attributeType: 'images' });
      await sendData({
        url: '/comments',
        method: 'post',
        data: submitData,
      });
      commentsData.update({ force: true });
      setShowCreateComment(false);
      setCommentState((prev) => ({ ...prev, attributes: { ...prev.attributes, body: '', images: [] } }));
    } catch (err) {
    } finally {
      setCommentLoading(false);
    }
  };

  return (
    <CommentContainer
      onSubmitComment={handleSubmitComment}
      data={props.checkpoint}
      commentableId={checkpointId}
      commentableType={'checkpoint'}
      commentState={commentState}
      comments={commentsData}
      setCommentState={setCommentState}
      showCreateComment={showCreateComment}
      onShowCreateComment={setShowCreateComment}
      loading={commentLoading}
      helperText={
        <Text variant="display-small">
          Checkpoint comments are only visible to <strong>content admins</strong>. The intended use is for suggesting modifications or having other administrative dialogue.
        </Text>
      }
      labelText="Comments"
    />
  );
});

export default CheckpointInfoModal;
